import React, { useMemo, useState } from 'react';
import { Box, Paper, useTheme, alpha, Tooltip, Menu, MenuItem } from '@mui/material';
import { IconButton, Typography } from '@sm-dashboard/ui-components';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SettingsIcon from '@mui/icons-material/Settings';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getJewishDay } from 'jewish-dates-core';
import dayjs, { type Dayjs } from 'dayjs';
import { HebrewDatePicker } from '@sm-dashboard/ui-components';
import { BasicJewishDay } from 'react-jewish-datepicker';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';
import { normalizePhone } from '@sm-dashboard/phone-utils';
import { getContactDisplayName } from '@/util/contactUtils';

export enum TimeRange {
  '1week' = '1week',
  '30days' = '30days',
  'custom' = 'custom'
}

interface UpcomingDate {
  id: string;
  title: string;
  date: Date;
  type: 'event' | 'meeting' | 'deadline' | 'reminder';
  contactId?: string;
  phone?: string;
  phoneCountry?: string;
  phoneWhatsapp?: boolean;
}

interface ContactDateFields {
  _id?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  phoneCountry?: string;
  phoneWhatsapp?: boolean;
  birthDate?: string | Date;
  jewishBirthDate?: string;
  weddingDate?: string | Date;
  jewishWeddingDate?: string;
  fatherYahrzeit?: string | Date;
  jewishFatherYahrzeit?: string;
  motherYahrzeit?: string | Date;
  jewishMotherYahrzeit?: string;
  deathDate?: string | Date;
  jewishDeathDate?: string;
  dynamicDates?: Array<{
    uuid: string;
    label: string;
    gregorianDate?: string | Date;
    jewishDate?: string;
  }>;
}

interface UpcomingDatesCardProps {
  contacts?: ContactDateFields[];
}

const UpcomingDatesCard: React.FC<UpcomingDatesCardProps> = ({ contacts = [] }) => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<TimeRange>(TimeRange['30days']);
  const [customStartDate, setCustomStartDate] = useState<string | null>(null);
  const [customEndDate, setCustomEndDate] = useState<string | null>(null);
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
  const [tempCustomStartDate, setTempCustomStartDate] = useState<BasicJewishDay | null>(null);
  const [tempCustomEndDate, setTempCustomEndDate] = useState<BasicJewishDay | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // המרה מ-BasicJewishDay ל-string (YYYY-MM-DD)
  const convertJewishDayToGregorianString = (jewishDay: BasicJewishDay | null): string => {
    if (!jewishDay || !jewishDay.date) return '';
    const date = new Date(jewishDay.date);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // המרה מ-string ל-BasicJewishDay
  const convertStringToJewishDay = (dateStr: string | null): BasicJewishDay | null => {
    if (!dateStr) return null;
    try {
      return getJewishDay(dayjs(dateStr) as Dayjs);
    } catch (error) {
      return null;
    }
  };
  const dateRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let endDate: Date = today;

    if (timeRange === TimeRange['custom']) {
      if (customStartDate && customEndDate) {
        endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999);
      } else {
        endDate = new Date(today);
        endDate.setDate(today.getDate() + 30);
      }
    } else {
      switch (timeRange) {
        case TimeRange['1week']:
          endDate = new Date(today);
          endDate.setDate(today.getDate() + 7);
          break;
        case TimeRange['30days']:
          endDate = new Date(today);
          endDate.setDate(today.getDate() + 30);
          break;
        default:
          endDate = new Date(today);
          endDate.setDate(today.getDate() + 30);
      }
    }
    return Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }, [timeRange, customStartDate, customEndDate]);
  const upcomingDates: UpcomingDate[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let startDate: Date = today;
    let futureDate: Date;
    if (timeRange === TimeRange['custom'] && customStartDate && customEndDate) {
      startDate = new Date(customStartDate);
      startDate.setHours(0, 0, 0, 0);
      futureDate = new Date(customEndDate);
      futureDate.setHours(23, 59, 59, 999);
    } else {
      futureDate = new Date(today);
      futureDate.setDate(today.getDate() + dateRange);
    }

    const dates: UpcomingDate[] = [];
    const extractDayMonthFromHebrewDate = (hebrewDateStr: string): { day: string; month: string } | null => {
      try {
        const normalized = hebrewDateStr.replace(/[׳״]/g, '');
        const match = normalized.match(/([א-ת]+)\s+([א-ת]+)/);
        if (match) {
          return { day: match[1], month: match[2] };
        }
        return null;
      } catch (error) {
        return null;
      }
    };
    const getAllOccurrencesFromHebrewDate = (hebrewDateStr: string | undefined): Date[] => {
      if (!hebrewDateStr) return [];
      try {
        const dayMonth = extractDayMonthFromHebrewDate(hebrewDateStr);
        if (!dayMonth) return [];
        const occurrences: Date[] = [];
        const currentDate = new Date(startDate);
        while (currentDate <= futureDate) {
          try {
            const jewishDay = getJewishDay(dayjs(currentDate) as Dayjs);
            if (jewishDay?.jewishDateStrHebrew) {
              const currentDayMonth = extractDayMonthFromHebrewDate(jewishDay.jewishDateStrHebrew);
              if (currentDayMonth && currentDayMonth.day === dayMonth.day && currentDayMonth.month === dayMonth.month) {
                occurrences.push(
                  new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0)
                );
              }
            }
          } catch (error) {
            // נמשיך אם יש שגיאה בתאריך מסוים
          }

          currentDate.setDate(currentDate.getDate() + 1);
        }
        return occurrences;
      } catch (error) {
        return [];
      }
    };
    const getAllOccurrencesFromGregorianDate = (dateStr: string | Date | undefined): Date[] => {
      if (!dateStr) return [];

      try {
        const originalDate = new Date(dateStr);
        if (isNaN(originalDate.getTime())) return [];
        const occurrences: Date[] = [];
        const originalMonth = originalDate.getMonth();
        const originalDay = originalDate.getDate();
        const endYear = futureDate.getFullYear();
        const startYear = startDate.getFullYear();
        for (let year = startYear; year <= endYear; year++) {
          const occurrence = new Date(year, originalMonth, originalDay, 0, 0, 0, 0);
          if (occurrence >= startDate && occurrence <= futureDate) {
            occurrences.push(occurrence);
          }
        }
        return occurrences;
      } catch (error) {
        return [];
      }
    };
    contacts.forEach(contact => {
      const contactName = getContactDisplayName(contact);
      const birthdays = contact.jewishBirthDate
        ? getAllOccurrencesFromHebrewDate(contact.jewishBirthDate)
        : getAllOccurrencesFromGregorianDate(contact.birthDate);
      birthdays.forEach((birthday, index) => {
        dates.push({
          id: `${contact._id}-birth-${index}`,
          title: `יום הולדת ל${contactName}`,
          date: birthday,
          type: 'event',
          contactId: contact._id,
          phone: contact.phone,
          phoneCountry: contact.phoneCountry,
          phoneWhatsapp: contact.phoneWhatsapp
        });
      });
      const weddings = contact.jewishWeddingDate
        ? getAllOccurrencesFromHebrewDate(contact.jewishWeddingDate)
        : getAllOccurrencesFromGregorianDate(contact.weddingDate);
      weddings.forEach((wedding, index) => {
        dates.push({
          id: `${contact._id}-wedding-${index}`,
          title: `יום נישואין ל${contactName}`,
          date: wedding,
          type: 'event',
          contactId: contact._id,
          phone: contact.phone,
          phoneCountry: contact.phoneCountry,
          phoneWhatsapp: contact.phoneWhatsapp
        });
      });
      const fatherYahrzeits = contact.jewishFatherYahrzeit
        ? getAllOccurrencesFromHebrewDate(contact.jewishFatherYahrzeit)
        : getAllOccurrencesFromGregorianDate(contact.fatherYahrzeit);
      fatherYahrzeits.forEach((yahrzeit, index) => {
        dates.push({
          id: `${contact._id}-father-yahrzeit-${index}`,
          title: `יארצייט אב ל${contactName}`,
          date: yahrzeit,
          type: 'reminder',
          contactId: contact._id,
          phone: contact.phone,
          phoneCountry: contact.phoneCountry,
          phoneWhatsapp: contact.phoneWhatsapp
        });
      });
      const motherYahrzeits = contact.jewishMotherYahrzeit
        ? getAllOccurrencesFromHebrewDate(contact.jewishMotherYahrzeit)
        : getAllOccurrencesFromGregorianDate(contact.motherYahrzeit);
      motherYahrzeits.forEach((yahrzeit, index) => {
        dates.push({
          id: `${contact._id}-mother-yahrzeit-${index}`,
          title: `יארצייט אמא ל${contactName}`,
          date: yahrzeit,
          type: 'reminder',
          contactId: contact._id,
          phone: contact.phone,
          phoneCountry: contact.phoneCountry,
          phoneWhatsapp: contact.phoneWhatsapp
        });
      });
      const deathDates = contact.jewishDeathDate
        ? getAllOccurrencesFromHebrewDate(contact.jewishDeathDate)
        : getAllOccurrencesFromGregorianDate(contact.deathDate);
      deathDates.forEach((deathDate, index) => {
        dates.push({
          id: `${contact._id}-death-${index}`,
          title: `יום פטירה ל${contactName}`,
          date: deathDate,
          type: 'reminder',
          contactId: contact._id,
          phone: contact.phone,
          phoneCountry: contact.phoneCountry,
          phoneWhatsapp: contact.phoneWhatsapp
        });
      });
      if (contact.dynamicDates && Array.isArray(contact.dynamicDates)) {
        contact.dynamicDates.forEach(dynamicDate => {
          const dynamicOccurrences = dynamicDate.jewishDate
            ? getAllOccurrencesFromHebrewDate(dynamicDate.jewishDate)
            : getAllOccurrencesFromGregorianDate(dynamicDate.gregorianDate);
          dynamicOccurrences.forEach((occurrence, index) => {
            dates.push({
              id: `${contact._id}-dynamic-${dynamicDate.uuid || Math.random()}-${index}`,
              title: `${dynamicDate.label || 'תאריך'} ל${contactName}`,
              date: occurrence,
              type: 'event',
              contactId: contact._id,
              phone: contact.phone,
              phoneCountry: contact.phoneCountry,
              phoneWhatsapp: contact.phoneWhatsapp
            });
          });
        });
      }
    });

    return dates.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [contacts, dateRange, timeRange, customStartDate, customEndDate]);

  const formatDate = (date: Date): { hebrew: string; gregorian: string } => {
    // תאריך לועזי
    const gregorianDate = date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    let hebrewDate = '';
    try {
      const jewishDay = getJewishDay(dayjs(date) as Dayjs);
      if (jewishDay?.jewishDateStrHebrew) {
        hebrewDate = jewishDay.jewishDateStrHebrew;
      }
    } catch (e) {
      // אם יש שגיאה, נשאיר ריק
    }

    return {
      hebrew: hebrewDate,
      gregorian: gregorianDate
    };
  };

  const buildWhatsappUrl = (phone: string | undefined, phoneCountry: string | undefined): string | null => {
    if (!phone) return null;
    try {
      const normalized = normalizePhone(phone, phoneCountry || 'IL');
      const sanitized = normalized?.e164?.replace(/\D/g, '') || phone.replace(/\D/g, '');
      if (!sanitized) return null;
      return `https://wa.me/${sanitized}`;
    } catch (error) {
      return null;
    }
  };

  const getEmptyMessage = (): { main: string; sub: string } => {
    switch (timeRange) {
      case TimeRange['1week']:
        return {
          main: 'אין תאריכים להצגה השבוע',
          sub: '(באפשרותך לבחור טווח תאריכים למטה)'
        };
      case TimeRange['30days']:
        return {
          main: 'אין תאריכים להצגה החודש',
          sub: '(באפשרותך לבחור טווח תאריכים למטה)'
        };
      case TimeRange['custom']:
        return {
          main: 'אין תאריכים להצגה בטווח הנבחר',
          sub: '(באפשרותך לבחור טווח תאריכים אחר)'
        };
      default:
        return {
          main: 'אין תאריכים קרובים להצגה',
          sub: ''
        };
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', mb: { xs: 5, md: 0 } }}>
      <Box
        sx={{
          bgcolor: 'info.main',
          color: 'common.white',
          p: 1,
          pl: 2,
          borderRadius: '30px 30px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexDirection: 'row',
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          <Typography variant="h6">תאריכים קרובים</Typography>
        </Box>
      </Box>

      <Paper
        sx={{
          borderRadius: '0 0 20px 20px',
          overflow: 'hidden',
          bgcolor: alpha(theme.palette.grey[300], 0.5),
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 280, sm: 400 },
          height: { xs: 'auto', sm: 400 },
          maxHeight: { xs: 400, sm: 'none' },
          p: { xs: 1, sm: 2 }
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          {upcomingDates.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 4
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {(() => {
                  const message = getEmptyMessage();
                  return (
                    <>
                      <Box sx={{ color: 'text.secondary' }}>
                        <Typography variant="body">{message.main}</Typography>
                      </Box>
                      {message.sub && (
                        <Box sx={{ color: 'text.secondary' }}>
                          <Typography variant="body">{message.sub}</Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
            </Box>
          ) : (
            upcomingDates.slice(0, 20).map(date => {
              const formattedDate = formatDate(date.date);
              return (
                <Box
                  key={date.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 1,
                    bgcolor: 'common.white'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 0.25,
                      flexShrink: 0
                    }}
                  >
                    {formattedDate.hebrew && (
                      <Box sx={{ pl: { xs: 0.5, sm: 2 } }}>
                        <Typography variant="body" color="text.secondary">
                          {formattedDate.hebrew}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ pl: { xs: 0.5, sm: 2 } }}>
                      <Typography variant="body" color="text.secondary">
                        {formattedDate.gregorian}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flex: 1,
                      minWidth: 0,
                      pl: { xs: 0, sm: 5 }
                    }}
                  >
                    <Box
                      sx={{
                        textAlign: 'left',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Typography variant="subtitle2" color="text.primary">
                        {date.title}
                      </Typography>
                    </Box>
                    {date.phone && buildWhatsappUrl(date.phone, date.phoneCountry) && (
                      <Tooltip title="שלח וואטסאפ">
                        <Box
                          component="a"
                          href={buildWhatsappUrl(date.phone, date.phoneCountry) || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'inline-flex', flexShrink: 0, color: 'success.main' }}
                        >
                          <IconButton size="small" color="secondary" aria-label="שלח וואטסאפ">
                            <WhatsAppIcon sx={{ fontSize: { xs: 22, sm: 30 } }} />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1
          }}
        >
          <Tooltip title="בחר טווח תצוגה">
            <IconButton size="small" color="info" shape="circle" aria-label="בחר טווח תצוגה" onClick={handleClick}>
              <SettingsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
          >
            <MenuItem
              onClick={() => {
                setTimeRange(TimeRange['1week']);
                handleClose();
              }}
              selected={timeRange === TimeRange['1week']}
            >
              שבוע הקרוב
            </MenuItem>
            <MenuItem
              onClick={() => {
                setTimeRange(TimeRange['30days']);
                handleClose();
              }}
              selected={timeRange === TimeRange['30days']}
            >
              חודש הקרוב
            </MenuItem>
            <MenuItem
              onClick={() => {
                // אם יש תאריכים שמורים, נשתמש בהם, אחרת נשתמש בתאריך של היום
                const today = new Date();
                const todayJewish = getJewishDay(dayjs(today) as Dayjs);
                setTempCustomStartDate(customStartDate ? convertStringToJewishDay(customStartDate) : todayJewish);
                setTempCustomEndDate(customEndDate ? convertStringToJewishDay(customEndDate) : todayJewish);
                setDateRangeDialogOpen(true);
                handleClose();
              }}
              selected={timeRange === TimeRange['custom']}
            >
              בחר טווח תאריכים
            </MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* Date Range Dialog */}
      <DynamicDialog
        open={dateRangeDialogOpen}
        title="בחירת טווח תאריכים"
        subtitle=""
        allowOverflow={true}
        handleNotOkClose={() => {
          setDateRangeDialogOpen(false);
          // Reset temp values on cancel
          const today = new Date();
          const todayJewish = getJewishDay(dayjs(today) as Dayjs);
          setTempCustomStartDate(customStartDate ? convertStringToJewishDay(customStartDate) : todayJewish);
          setTempCustomEndDate(customEndDate ? convertStringToJewishDay(customEndDate) : todayJewish);
        }}
        handleOkClose={() => {
          if (tempCustomStartDate && tempCustomEndDate) {
            setCustomStartDate(convertJewishDayToGregorianString(tempCustomStartDate));
            setCustomEndDate(convertJewishDayToGregorianString(tempCustomEndDate));
            setTimeRange(TimeRange['custom']);
          }
          setDateRangeDialogOpen(false);
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mt: 2,
            minWidth: { xs: 'auto', sm: 300 },
            overflow: 'visible'
          }}
        >
          <Box sx={{ overflow: 'visible', zIndex: 1301 }}>
            <HebrewDatePicker
              appearance="plain"
              label="תאריך התחלה"
              hebrewDate={tempCustomStartDate?.jewishDateStrHebrew ?? ''}
              gregorianDate={convertJewishDayToGregorianString(tempCustomStartDate) || undefined}
              onHebrewDateChange={() => undefined}
              onGregorianDateChange={gregorian => {
                setTempCustomStartDate(convertStringToJewishDay(gregorian));
              }}
              onJewishDaySelect={setTempCustomStartDate}
              fullWidth
            />
          </Box>
          <Box sx={{ overflow: 'visible', zIndex: 1300 }}>
            <HebrewDatePicker
              appearance="plain"
              label="תאריך סיום"
              hebrewDate={tempCustomEndDate?.jewishDateStrHebrew ?? ''}
              gregorianDate={convertJewishDayToGregorianString(tempCustomEndDate) || undefined}
              onHebrewDateChange={() => undefined}
              onGregorianDateChange={gregorian => {
                setTempCustomEndDate(convertStringToJewishDay(gregorian));
              }}
              onJewishDaySelect={setTempCustomEndDate}
              fullWidth
            />
          </Box>
        </Box>
      </DynamicDialog>
    </Box>
  );
};

export default UpcomingDatesCard;
